import * as SBService from "helpers/sportbookService";
import LeagueInterface, {
  BracketLeagueDetailInterface,
} from "interfaces/league";
import moment, { ISO_8601 } from "moment";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

const acquireDate = (date: string): moment.Moment => {
  return moment(date, ISO_8601);
};

export const useLeagueDetail = (
  id: string,
  fallback: LeagueInterface
): LeagueInterface => {
  const [league, setLeague] = useState(fallback);

  const fetcher = async (id: string) => {
    let leagueData: LeagueInterface;

    const rsp = await SBService.getLeagueById(id);

    if (rsp.data) {
      leagueData = rsp.data;
    } else {
      leagueData = rsp;
    }

    if (!leagueData) throw Error("League not found");

    const { startDate, endDate, startRegistrationDate, endRegistrationDate, startPairingDate, endPairingDate } =
      leagueData;

    leagueData.startDateObj = acquireDate(startDate);
    leagueData.endDateObj = acquireDate(endDate);
    leagueData.endDateObj = leagueData.endDateObj.endOf('day');
    leagueData.startRegistrationDateObj = acquireDate(startRegistrationDate);
    leagueData.endRegistrationDateObj = acquireDate(endRegistrationDate);
    leagueData.endRegistrationDateObj = leagueData.endRegistrationDateObj.endOf('day');
    leagueData.startPairingDateObj = acquireDate(startPairingDate);
    leagueData.endPairingDateObj = acquireDate(endPairingDate);
    leagueData.endPairingDateObj = leagueData.endPairingDateObj.endOf('day');
    setLeague(leagueData);
  };

  useEffect(() => {
    (async () => fetcher(id))();
  }, [id]);

  return league;
};

export interface MyLeagueStatus {
  registered: boolean;
  state: string;
  requiresFee: boolean;
  feePaid: boolean;
  fee: number | null;
  /** Hạn đóng phí của giải - backend trả từ leagues.feeDeadlineDate. */
  feeDeadline?: string | null;
  /** Cơ thủ đã tải ảnh biên lai chuyển khoản lên hay chưa. */
  billUploaded?: boolean;
  leaguePlayerId?: string;
  isCheckin?: boolean;
}

/**
 * Trạng thái đăng ký của tôi trong giải - chủ yếu để biết đã được ban tổ chức
 * xác nhận đóng phí hay còn chờ. Chưa đăng nhập hoặc lỗi mạng thì trả null và
 * trang hiển thị như giải không thu phí.
 */
export const useMyLeagueStatus = (leagueId: string, enabled: boolean = true) => {
  const [status, setStatus] = useState<MyLeagueStatus | null>(null);

  const fetcher = async () => {
    if (!leagueId || !enabled) return;
    try {
      const rsp = await SBService.getMyLeagueStatus(leagueId);
      setStatus(rsp?.data ?? rsp ?? null);
    } catch (err) {
      setStatus(null);
    }
  };

  useEffect(() => {
    (async () => fetcher())();
  }, [leagueId, enabled]);

  return status;
};

export const useBracketLeagueDetail = (leagueId: string) => {
  const [league, setLeague] = useState<BracketLeagueDetailInterface>();
  const fetcher = async () => {
    try {
      const data = await SBService.getBracketLeagueInfo(leagueId);
      setLeague(data?.data);
    } catch (err: any) {
      if (err.response && err.response.data && err.response.data.error) {
        return toast.error("Lỗi, " + err.response.data.error);
      }

      return toast.error("Không thể tải thông tin cặp đấu loại trực tiếp");
    }
  };

  useEffect(() => {
    (async () => fetcher())();
  }, [leagueId]);

  return league;
};
